// Phase 36 Batch AM — Single discovered listing card.
//
// Renders:
//   • address + city, state
//   • price + property type badge
//   • beds/baths/sqft
//   • cap rate + cash flow (colour-coded green/amber/rose)
//   • days on market / list date
//   • CTAs: "Add as Deal" (primary), "Dismiss" (link, sessionStorage)
//
// `onImport` and `onDismiss` are passed in so the parent owns business
// logic — this component is pure presentation + click delegation.

import { useState } from "react";
import { ExternalLink, X, Bed, Bath, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackListingViewed } from "@/lib/discoveryEvents";

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function _capToneClass(cap) {
  if (cap === null || cap === undefined) return "text-slate-500 bg-slate-50 border-slate-200";
  if (cap >= 8) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (cap >= 5) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-rose-700 bg-rose-50 border-rose-200";
}

function _cfToneClass(cf) {
  if (cf === null || cf === undefined) return "text-slate-500 bg-slate-50 border-slate-200";
  if (cf >= 200) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (cf >= 0) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-rose-700 bg-rose-50 border-rose-200";
}

function _ptLabel(t) {
  if (!t) return "Property";
  const s = String(t).toLowerCase();
  if (s.includes("condo")) return "Condo";
  if (s.includes("town")) return "Townhouse";
  if (s.includes("multi") || s.includes("duplex") || s.includes("triplex") || s.includes("fourplex"))
    return "Multifamily";
  if (s.includes("commercial")) return "Commercial";
  return "Single Family";
}

export default function ListingCard({ listing, onImport, onDismiss }) {
  const [importing, setImporting] = useState(false);
  const cap = listing?.estimated_cap_rate_pct;
  const cf = listing?.estimated_cash_flow_monthly;
  const dom = listing?.days_on_market;

  // Fire posthog "viewed" event once on first render. We don't have an
  // observer here — view = card rendered into the DOM.
  useState(() => {
    trackListingViewed({
      listing_id: listing?.rentcast_id,
      cap_rate: cap,
      cash_flow: cf,
    });
  });

  const handleImport = async () => {
    if (importing) return;
    setImporting(true);
    try {
      await onImport?.(listing);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      data-testid={`discovery-listing-card-${listing.rentcast_id}`}
      className="group flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {listing.address || "Address unknown"}
          </p>
          <p className="truncate text-xs text-slate-500">
            {[listing.city, listing.state].filter(Boolean).join(", ")}
            {listing.zip ? ` ${listing.zip}` : ""}
          </p>
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
          {_ptLabel(listing.property_type)}
        </span>
      </div>

      <div className="flex items-baseline justify-between">
        <p className="text-xl font-semibold tracking-tight text-slate-900">
          {money(listing.list_price)}
        </p>
        {dom !== null && dom !== undefined && (
          <p className="text-[11px] text-slate-500">{dom} days on market</p>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1">
          <Bed className="h-3.5 w-3.5" /> {Number(listing.beds ?? 0)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Bath className="h-3.5 w-3.5" /> {Number(listing.baths ?? 0)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Square className="h-3.5 w-3.5" />{" "}
          {listing.sqft ? `${Number(listing.sqft).toLocaleString()} sqft` : "—"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div
          className={`rounded-lg border px-3 py-2 ${_capToneClass(cap)}`}
          data-testid={`listing-cap-${listing.rentcast_id}`}
        >
          <p className="text-[10px] uppercase tracking-wide opacity-70">Cap rate</p>
          <p className="text-sm font-semibold tabular-nums">
            {cap !== null && cap !== undefined ? `${cap.toFixed(2)}%` : "—"}
          </p>
        </div>
        <div
          className={`rounded-lg border px-3 py-2 ${_cfToneClass(cf)}`}
          data-testid={`listing-cf-${listing.rentcast_id}`}
        >
          <p className="text-[10px] uppercase tracking-wide opacity-70">Cash flow</p>
          <p className="text-sm font-semibold tabular-nums">
            {cf !== null && cf !== undefined ? `${money(cf)}/mo` : "—"}
          </p>
        </div>
      </div>

      <div className="mt-1 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onDismiss?.(listing)}
          data-testid={`listing-dismiss-${listing.rentcast_id}`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" /> Dismiss
        </button>
        <Button
          type="button"
          size="sm"
          onClick={handleImport}
          disabled={importing}
          data-testid={`listing-import-${listing.rentcast_id}`}
          className="gap-1.5 bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {importing ? "Adding…" : "Add as Deal"}
        </Button>
      </div>
    </div>
  );
}
